/** Account profile url on an item payload (entity or DTO shapes). */
export function itemSellerUrl(item: {
  account?: { url?: string };
  accountById?: { url?: string };
  url?: string;
  itemUrl?: string;
} | null | undefined): string | null {
  if (!item) {
    return null;
  }
  if (item.account?.url) {
    return item.account.url;
  }
  if (item.accountById?.url) {
    return item.accountById.url;
  }
  // Latest-items Item DTO: `url` is seller profile, `itemUrl` is the listing slug.
  if (item.itemUrl && item.url) {
    return item.url;
  }
  return null;
}

/** Public URL segment for an item: title slug when present, else legacy UUID. */
export function itemUrlSegment(item: {
  itemUrl?: string;
  url?: string;
  itemId?: string;
  account?: { url?: string };
  accountById?: { url?: string };
} | null | undefined): string {
  if (!item) {
    return '';
  }
  if (item.itemUrl) {
    return item.itemUrl;
  }
  const sellerUrl = itemSellerUrl(item);
  // ItemEntity exposes slug on `url`; Item DTO uses `url` for the seller profile.
  if (item.url && item.url !== sellerUrl) {
    return item.url;
  }
  if (item.itemId) {
    return String(item.itemId);
  }
  return '';
}

type CategoryNode = { url?: string; name?: string } | null | undefined;

/** Taxonomy URL slugs from an item's basecategory (`bc.c` … `bc.ssssssss`). */
export function taxonomySegmentsFromBc(bc: {
  c?: CategoryNode;
  s?: CategoryNode;
  ss?: CategoryNode;
  sss?: CategoryNode;
  ssss?: CategoryNode;
  sssss?: CategoryNode;
  ssssss?: CategoryNode;
  sssssss?: CategoryNode;
  ssssssss?: CategoryNode;
} | null | undefined): string[] {
  if (!bc) {
    return [];
  }
  const levels: CategoryNode[] = [
    bc.c, bc.s, bc.ss, bc.sss, bc.ssss, bc.sssss, bc.ssssss, bc.sssssss, bc.ssssssss
  ];
  const segments: string[] = [];
  for (const node of levels) {
    const slug = node?.url || node?.name?.toLowerCase()?.replace(/\s+/g, '-');
    if (!slug) {
      break;
    }
    segments.push(String(slug).trim().toLowerCase());
  }
  return segments;
}

/** Router path for editing a listing: /update-item/<taxonomy...>/<itemId> */
export function buildUpdateListingRouterLink(item: {
  itemId?: string;
  bc?: Parameters<typeof taxonomySegmentsFromBc>[0];
} | null | undefined): string[] {
  const itemId = item?.itemId ? String(item.itemId) : '';
  const taxonomy = taxonomySegmentsFromBc(item?.bc);
  if (!itemId) {
    return ['/update-item'];
  }
  return ['/update-item', ...taxonomy, itemId];
}

/** Router navigation state carrying seller scope for slug-based item detail loads. */
export function itemNavState(item: Parameters<typeof itemSellerUrl>[0]): { endUser?: string } {
  const endUser = itemSellerUrl(item);
  return endUser ? { endUser } : {};
}
